import { useState, useEffect, useCallback, useMemo } from "react";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../constants/AppRoutes";
import { endpoints } from "../../../../constants/Endpoints";
import { CSVLink } from "react-csv";
import { Box, Button, TextField, IconButton } from "@mui/material";
import { OverviewHeaderBar } from "../../../common/OverviewHeaderBar";
import { SelectableDataGrid } from "../../../common/SelectableDataGrid";
import Swal from "sweetalert2"; // kept for toasts
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/nl";

// Icons
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import RedoIcon from "@mui/icons-material/Redo";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { ScrollableDataGrid } from "../../../common/ScrollableDataGrid";

export function RenterArchiveTable(props) {
  const styles = {
    exportToCsvButton: {
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        color: "#d01717",
      },
    },
    exportToCsvLink: {
      color: "white",
      textDecoration: "none",
    },
    newButton: {
      backgroundColor: "#eb008b",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    refreshButton: {
      backgroundColor: "#f68121",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      marginLeft: "20px",
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
  };

  const refreshData = () => {
    setLoading(true);
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });
  };

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.renter.getArchive,
      null
    );
    return await response.json();
  }, []);

  const deleteData = async (id) => {
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.renter.delete + id,
      null
    );
    return response.ok;
  };

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectionCount, setSelectionCount] = useState(0); // track bulk selection
  const [selectedDate, setSelectedDate] = useState(null); // retained for compatibility; will remove picker

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Acties",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (selectionCount > 0) return null; // hide row actions during bulk selection
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                gap: 0.5,
              }}
            >
              <IconButton
                size="small"
                title="Verleng inschrijving"
                onClick={(e) => {
                  e.stopPropagation();
                  startSubscriptionRenewal(params.row.id);
                }}
              >
                <RedoIcon fontSize="inherit" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                title="Permanent verwijderen"
                onClick={(e) => {
                  e.stopPropagation();
                  Swal.fire({
                    title: "Permanent verwijderen",
                    text: "Weet je zeker dat je deze huurder permanent wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#ec2027",
                    confirmButtonText: "Verwijderen",
                    cancelButtonText: "Annuleren",
                  }).then((res) => {
                    if (res.isConfirmed) {
                      deleteData(params.row.id).then((ok) => {
                        if (ok) {
                          const Toast = Swal.mixin({
                            toast: true,
                            position: "bottom-end",
                            showConfirmButton: false,
                            timer: 2500,
                            timerProgressBar: true,
                          });
                          Toast.fire({
                            icon: "success",
                            title: "Huurder verwijderd",
                          });
                          refreshData();
                        } else {
                          Swal.fire({
                            icon: "error",
                            title: "Verwijderen mislukt",
                            text: "De huurder kon niet worden verwijderd.",
                          });
                        }
                      });
                    }
                  });
                }}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Box>
          );
        },
      },
      {
        field: "applicationUserFirstName",
        headerName: "Voornaam",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "applicationUserLastName",
        headerName: "Achternaam",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "applicationUserEmail",
        headerName: "E-mail",
        flex: 1.2,
        minWidth: 220,
      },
      {
        field: "applicationUserPhoneNumber",
        headerName: "Telefoonnummer",
        flex: 0.8,
        minWidth: 160,
      },
    ],
    [selectionCount]
  );

  const csvHeaders = [
    { label: "Achternaam", key: "applicationUserLastName" },
    { label: "Voornaam", key: "applicationUserFirstName" },
    { label: "Bedrijfsnaam", key: "applicationUserCompanyName" },
    { label: "Telefoonnummer", key: "applicationUserPhoneNumber" },
    { label: "Email", key: "applicationUserEmail" },
    { label: "Stad", key: "city" },
    { label: "Straat", key: "street" },
    { label: "Huisnummer", key: "houseNumber" },
    { label: "Postcode", key: "zipCode" },
    { label: "Toevoeging", key: "addition" },
    { label: "Netto inkomen per maand", key: "netIncomePerMonth" },
    { label: "Maximum prijs", key: "preferredMaxPrice" },
    { label: "Voorkeur stad", key: "preferredCity" },
    { label: "Voorkeur interieur", key: "preferredInterior" },
    { label: "Voorkeur wijk", key: "preferredDistrict" },
    {
      label: "Minimum aantal slaapkamers",
      key: "preferredMinNumberOfBedrooms",
    },
    { label: "Wil huren vanaf", key: "preferredStartOfRentingDate" },
    { label: "Overige wensen", key: "wishes" },
    { label: "Taalcode", key: "applicationUserLocale" },
  ];

  const [isMounted, setMounted] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });

    return () => {
      setMounted(false);
    };
  }, [isMounted, getData]);

  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (r) =>
        r.applicationUserFirstName?.toLowerCase().includes(s) ||
        r.applicationUserLastName?.toLowerCase().includes(s) ||
        r.applicationUserEmail?.toLowerCase().includes(s) ||
        r.applicationUserPhoneNumber?.toLowerCase().includes(s)
    );
  }, [data, search]);

  const onRowClick = (params) => {
    if (selectionCount > 0) return; // disable navigation in selection mode
    navigate(appRoutes.shb.archive.detail + params.row.id);
  };

  const [currentId, setCurrentId] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDate, setRenewDate] = useState(moment());
  const startSubscriptionRenewal = (id) => {
    setCurrentId(id);
    setRenewDate(moment());
    setRenewOpen(true);
  };
  const closeRenewDialog = () => setRenewOpen(false);
  const submitRenewal = async () => {
    if (!renewDate || !renewDate.isValid()) {
      setRenewOpen(false);
      return;
    }
    setLoading(true);
    const dateIso = renewDate.clone().startOf("day").toISOString();
    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.renter.renewSubscription,
      { renterId: currentId, newDate: dateIso }
    );
    const message = await response.json();
    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({ icon: message.icon, title: message.title });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    const newData = await getData();
    setData(newData);
    setLoading(false);
    setRenewOpen(false);
  };

  const bulkDelete = async (selection) => {
    const ids = Array.from(selection.ids);

    if (!ids?.length) return;

    const confirm = await Swal.fire({
      title: "Permanent verwijderen",
      text: `Weet je zeker dat je ${ids.length} huurder(s) permanent wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ec2027",
      confirmButtonText: "Verwijderen",
      cancelButtonText: "Annuleren",
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);

    try {
      const response = await HttpClient.doRequest(
        Methods.POST,
        endpoints.renter.bulkDelete,
        { renterIds: ids }
      );
      const message = await response.json().catch(() => null);
      if (response.ok) {
        const Toast = Swal.mixin({
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        });

        Toast.fire({
          icon: message?.icon || "success",
          title: message?.title || "Geselecteerde huurders verwijderd",
        });
      } else {
        Swal.fire({
          icon: message?.icon || "error",
          title: message?.title || "Verwijderen mislukt",
          text: message?.content || "Er is iets misgegaan.",
          confirmButtonColor: "#888",
        });
      }
    } finally {
      refreshData();
    }
  };

  return (
    <>
      <Dialog
        open={renewOpen}
        onClose={closeRenewDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Verleng inschrijving</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="nl">
            <DatePicker
              label="Nieuwe vervaldatum"
              value={renewDate}
              format="DD/MM/YYYY"
              onChange={(m) => {
                if (m && m.isValid()) setRenewDate(m);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: {
                    "& .MuiInputBase-root": { backgroundColor: "transparent" },
                    "& .MuiFormLabel-root": { backgroundColor: "transparent" },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenewDialog}>Annuleren</Button>
          <Button onClick={submitRenewal} variant="contained" color="primary">
            Opslaan
          </Button>
        </DialogActions>
      </Dialog>
      <OverviewHeaderBar
        title="Inschrijvingen archief"
        actions={
          selectionCount > 0 ? null : (
            <>
              <TextField
                size="small"
                label="Zoeken"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <CSVLink
                data={data}
                style={styles.exportToCsvLink}
                headers={csvHeaders}
                filename="archief_huurders.csv"
              >
                <Button
                  variant="contained"
                  startIcon={<SystemUpdateAltIcon />}
                  style={styles.exportToCsvButton}
                >
                  Download CSV
                </Button>
              </CSVLink>
              <Button
                variant="contained"
                style={styles.refreshButton}
                startIcon={<RefreshIcon />}
                onClick={refreshData}
              >
                <span>Vernieuw</span>
              </Button>
            </>
          )
        }
      />
      <Box sx={{ mx: 3, mb: 3 }}>
        <SelectableDataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r.id}
          onRowClick={onRowClick}
          onRowSelectionModelChange={(model) => {
            setSelectionCount(model?.ids?.size || 0);
          }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: "applicationUserLastName", sort: "asc" }],
            },
          }}
          dataGridSx={{ backgroundColor: "background.paper" }}
          bulkBarSx={{ mx: 3 }}
          renderBulkActions={({ selection, clearSelection }) => (
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={async () => {
                await bulkDelete(selection);
                clearSelection();
              }}
            >
              Verwijder geselecteerd
            </Button>
          )}
        />
      </Box>
    </>
  );
}
